import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Plus, Trash2, FileText, AlertCircle, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { ChangeOrderSuggestions } from './ChangeOrderSuggestions';
import { parseChangeOrderForConfigItems } from '../utils/changeOrderParser';
import type { SOW, ChangeOrder, SuggestedConfigItem } from '../types';

interface SOWDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (sow: Omit<SOW, 'id'>) => void;
  editSOW?: SOW;
}

export function SOWDialog({ open, onClose, onSave, editSOW }: SOWDialogProps) {
  const [sowId, setSOWId] = useState('');
  const [scopeDescription, setScopeDescription] = useState('');
  const [status, setStatus] = useState<'draft' | 'active' | 'completed' | 'cancelled'>('draft');
  const [notes, setNotes] = useState('');
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [originalValues, setOriginalValues] = useState<any>(null);
  
  // Change Order form state
  const [showAddChangeOrder, setShowAddChangeOrder] = useState(false);
  const [coId, setCoId] = useState('');
  const [coDescription, setCoDescription] = useState('');
  const [coScopeChanges, setCoScopeChanges] = useState('');
  const [coStatus, setCoStatus] = useState<'draft' | 'pending_approval' | 'approved' | 'applied' | 'billed' | 'rejected' | 'cancelled'>('draft');
  const [coNotes, setCoNotes] = useState('');
  const [editingCoIndex, setEditingCoIndex] = useState<number | null>(null);
  const [coSuggestions, setCoSuggestions] = useState<SuggestedConfigItem[]>([]);

  useEffect(() => {
    if (editSOW) {
      const values = {
        sowId: editSOW.sowId,
        scopeDescription: editSOW.scopeDescription,
        status: editSOW.status,
        notes: editSOW.notes || '',
        changeOrders: editSOW.changeOrders || []
      };
      setSOWId(values.sowId);
      setScopeDescription(values.scopeDescription);
      setStatus(values.status);
      setNotes(values.notes);
      setChangeOrders(values.changeOrders);
      setOriginalValues(values);
    } else {
      setSOWId('');
      setScopeDescription('');
      setStatus('draft');
      setNotes('');
      setChangeOrders([]);
      setOriginalValues(null);
    }
    setShowAddChangeOrder(false);
    resetChangeOrderForm();
  }, [editSOW, open]);

  const resetChangeOrderForm = () => {
    setCoId('');
    setCoDescription('');
    setCoScopeChanges('');
    setCoStatus('draft');
    setCoNotes('');
    setEditingCoIndex(null);
    setCoSuggestions([]);
  };

  const handleAnalyzeScopeChanges = () => {
    if (!coScopeChanges.trim() || !coDescription.trim()) {
      alert('Please enter a description and scope changes to analyze');
      return;
    }

    const { suggestedItems } = parseChangeOrderForConfigItems(
      coScopeChanges,
      coDescription
    );
    setCoSuggestions(suggestedItems);
  };

  const handleAcceptSuggestion = (suggestionId: string) => {
    setCoSuggestions(prev => 
      prev.map(s => s.id === suggestionId ? { ...s, accepted: true } : s)
    );
  };

  const handleRejectSuggestion = (suggestionId: string) => {
    setCoSuggestions(prev => prev.filter(s => s.id !== suggestionId));
  };

  const handleAcceptAllSuggestions = () => {
    setCoSuggestions(prev => prev.map(s => ({ ...s, accepted: true })));
  };

  const handleAddChangeOrder = () => {
    if (!coId.trim() || !coDescription.trim() || !coScopeChanges.trim()) {
      alert('Please fill in Change Order ID, description, and scope changes');
      return;
    }

    const newChangeOrder: ChangeOrder = {
      id: Date.now().toString(),
      changeOrderId: coId.trim(),
      description: coDescription.trim(),
      scopeChanges: coScopeChanges.trim(),
      dateAdded: new Date().toISOString(),
      status: coStatus,
      notes: coNotes.trim() || undefined,
      suggestedConfigItems: coSuggestions.length > 0 ? coSuggestions : undefined
    };

    if (editingCoIndex !== null) {
      const updated = [...changeOrders];
      updated[editingCoIndex] = newChangeOrder;
      setChangeOrders(updated);
    } else {
      setChangeOrders([...changeOrders, newChangeOrder]);
    }

    resetChangeOrderForm();
    setShowAddChangeOrder(false);
  };

  const handleEditChangeOrder = (index: number) => {
    const co = changeOrders[index];
    setCoId(co.changeOrderId);
    setCoDescription(co.description);
    setCoScopeChanges(co.scopeChanges);
    setCoStatus(co.status);
    setCoNotes(co.notes || '');
    setCoSuggestions(co.suggestedConfigItems || []);
    setEditingCoIndex(index);
    setShowAddChangeOrder(true);
  };

  const handleDeleteChangeOrder = (index: number) => {
    if (confirm('Are you sure you want to delete this change order?')) {
      setChangeOrders(changeOrders.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sowId.trim()) {
      alert('Please enter an SOW ID');
      return;
    }
    
    if (!scopeDescription.trim()) {
      alert('Please enter a scope description');
      return;
    }

    onSave({
      sowId: sowId.trim(),
      scopeDescription: scopeDescription.trim(),
      createdDate: editSOW?.createdDate || new Date().toISOString(),
      status,
      changeOrders,
      notes: notes.trim() || undefined
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      case 'pending_approval': return 'bg-yellow-500';
      case 'approved': return 'bg-green-500';
      case 'applied': return 'bg-blue-500';
      case 'billed': return 'bg-purple-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editSOW ? 'Edit Statement of Work' : 'New Statement of Work'}</DialogTitle>
          <DialogDescription>
            {editSOW 
              ? 'Update the SOW details, scope, and change orders for this statement of work.' 
              : 'Create a new Statement of Work to track project scope, deliverables, and change orders.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Basic SOW Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sowId">SOW ID *</Label>
                <Input
                  id="sowId"
                  value={sowId}
                  onChange={(e) => setSOWId(e.target.value)}
                  placeholder="e.g., SOW-2024-001, SOW-ABC-123"
                  required
                />
                <p className="text-muted-foreground">Alphanumeric identifier for this SOW</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select value={status} onValueChange={(value: any) => setStatus(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Scope Description */}
            <div className="space-y-2">
              <Label htmlFor="scope">Scope of Work *</Label>
              <Textarea
                id="scope"
                value={scopeDescription}
                onChange={(e) => setScopeDescription(e.target.value)}
                placeholder="Paste the complete scope of work here. This can include what's in scope, out of scope, deliverables, timelines, etc. You can copy/paste from the SOW document."
                rows={12}
                required
                className="font-mono"
              />
              <p className="text-muted-foreground">Copy and paste the scope section from your SOW document, or type the complete scope details here.</p>
            </div>

            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                <strong>Tip:</strong> You can paste the entire SOW text here for reference. In the future, we'll add PDF upload and AI-powered scope extraction to automatically identify what's in and out of scope.
              </AlertDescription>
            </Alert>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes, clarifications, or important details about this SOW"
                rows={3}
              />
            </div>

            <Separator />

            {/* Change Orders Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3>Change Orders</h3>
                  <p className="text-muted-foreground">Track scope changes and additions to this SOW</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    resetChangeOrderForm();
                    setShowAddChangeOrder(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Change Order
                </Button>
              </div>

              {/* Change Orders List */}
              {changeOrders.length > 0 && (
                <div className="space-y-3">
                  {changeOrders.map((co, index) => (
                    <Card key={co.id}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2">
                              {co.changeOrderId}
                              <Badge className={getStatusColor(co.status)}>
                                {co.status}
                              </Badge>
                            </CardTitle>
                            <CardDescription>{co.description}</CardDescription>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditChangeOrder(index)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteChangeOrder(index)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div>
                          <p className="text-muted-foreground">Scope Changes:</p>
                          <p className="mt-1 whitespace-pre-wrap">{co.scopeChanges}</p>
                        </div>
                        {co.notes && (
                          <div>
                            <p className="text-muted-foreground">Notes:</p>
                            <p className="mt-1">{co.notes}</p>
                          </div>
                        )}
                        <p className="text-muted-foreground">
                          Added: {new Date(co.dateAdded).toLocaleDateString()}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Add/Edit Change Order Form */}
              {showAddChangeOrder && (
                <Card className="border-primary">
                  <CardHeader>
                    <CardTitle>
                      {editingCoIndex !== null ? 'Edit Change Order' : 'Add Change Order'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="coId">Change Order ID *</Label>
                        <Input
                          id="coId"
                          value={coId}
                          onChange={(e) => setCoId(e.target.value)}
                          placeholder="e.g., CO-001, CHG-2024-001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="coStatus">Status *</Label>
                        <Select value={coStatus} onValueChange={(value: any) => setCoStatus(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="pending_approval">Pending Approval</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="applied">Applied</SelectItem>
                            <SelectItem value="billed">Billed</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                            <SelectItem value="cancelled">Canceled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coDescription">Description *</Label>
                      <Input
                        id="coDescription"
                        value={coDescription}
                        onChange={(e) => setCoDescription(e.target.value)}
                        placeholder="Brief description of this change order"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coScopeChanges">Scope Changes *</Label>
                      <Textarea
                        id="coScopeChanges"
                        value={coScopeChanges}
                        onChange={(e) => setCoScopeChanges(e.target.value)}
                        placeholder="Detailed description of what's being added or changed in scope"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="coNotes">Notes</Label>
                      <Textarea
                        id="coNotes"
                        value={coNotes}
                        onChange={(e) => setCoNotes(e.target.value)}
                        placeholder="Additional notes about this change order"
                        rows={2}
                      />
                    </div>

                    {/* AI Analysis Button */}
                    {coScopeChanges.trim() && coDescription.trim() && coSuggestions.length === 0 && (
                      <Alert>
                        <Sparkles className="h-4 w-4" />
                        <AlertDescription className="flex items-center justify-between">
                          <span>
                            Analyze this change order to get AI-suggested configuration items
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAnalyzeScopeChanges}
                          >
                            <Sparkles className="mr-2 h-4 w-4" />
                            Analyze Scope
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* AI Suggestions */}
                    {coSuggestions.length > 0 && (
                      <ChangeOrderSuggestions
                        suggestions={coSuggestions}
                        onAcceptSuggestion={handleAcceptSuggestion}
                        onRejectSuggestion={handleRejectSuggestion}
                        onAcceptAll={handleAcceptAllSuggestions}
                      />
                    )}

                    <div className="flex gap-2">
                      <Button type="button" onClick={handleAddChangeOrder}>
                        {editingCoIndex !== null ? 'Update Change Order' : 'Add Change Order'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          resetChangeOrderForm();
                          setShowAddChangeOrder(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {changeOrders.length === 0 && !showAddChangeOrder && (
                <p className="text-muted-foreground text-center py-4">
                  No change orders yet. Add one to track scope changes to this SOW.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!sowId.trim() || !scopeDescription.trim() || (editSOW && originalValues && !(
              sowId.trim() !== originalValues.sowId ||
              scopeDescription.trim() !== originalValues.scopeDescription ||
              status !== originalValues.status ||
              notes.trim() !== originalValues.notes ||
              JSON.stringify(changeOrders) !== JSON.stringify(originalValues.changeOrders)
            ))}>
              {editSOW ? 'Save Changes' : 'Create SOW'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
